import { useCallback, useState } from "react";

import type { SearchResourceName } from "../types";

/**
 * The last searches run and results opened from the global search, newest
 * first, kept on this device so the empty search screen has somewhere to go.
 */

export const RECENT_SEARCHES_KEY = "avante.search.recent";
export const MAX_RECENT_SEARCHES = 6;

export type RecentSearch =
  | { type: "query"; query: string }
  | {
      type: "result";
      resource: SearchResourceName;
      title: string;
      url: string;
    };

const isRecentSearch = (value: unknown): value is RecentSearch => {
  if (typeof value !== "object" || value == null) return false;
  const entry = value as Record<string, unknown>;
  if (entry.type === "query") return typeof entry.query === "string";
  return (
    entry.type === "result" &&
    typeof entry.resource === "string" &&
    typeof entry.title === "string" &&
    typeof entry.url === "string"
  );
};

const sameEntry = (a: RecentSearch, b: RecentSearch): boolean =>
  a.type === "query" && b.type === "query"
    ? a.query.toLowerCase() === b.query.toLowerCase()
    : a.type === "result" && b.type === "result" && a.url === b.url;

export const readRecentSearches = (): RecentSearch[] => {
  try {
    const parsed: unknown = JSON.parse(
      localStorage.getItem(RECENT_SEARCHES_KEY) ?? "[]",
    );
    return Array.isArray(parsed)
      ? parsed.filter(isRecentSearch).slice(0, MAX_RECENT_SEARCHES)
      : [];
  } catch {
    // corrupted or unavailable storage: start over rather than break search
    return [];
  }
};

/** `entry` first, without its older copy, capped to MAX_RECENT_SEARCHES. */
export const addRecentSearch = (
  entries: readonly RecentSearch[],
  entry: RecentSearch,
): RecentSearch[] =>
  [entry, ...entries.filter((other) => !sameEntry(other, entry))].slice(
    0,
    MAX_RECENT_SEARCHES,
  );

const write = (entries: RecentSearch[]) => {
  try {
    if (entries.length === 0) {
      localStorage.removeItem(RECENT_SEARCHES_KEY);
    } else {
      localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(entries));
    }
  } catch {
    // private mode / quota: recents are a convenience, not data
  }
};

export const useRecentSearches = () => {
  const [recent, setRecent] = useState<RecentSearch[]>(readRecentSearches);

  const remember = useCallback((entry: RecentSearch) => {
    const next = addRecentSearch(readRecentSearches(), entry);
    write(next);
    setRecent(next);
  }, []);

  /** Re-read storage, e.g. on open: another tab may have searched since. */
  const refresh = useCallback(() => setRecent(readRecentSearches()), []);

  const clear = useCallback(() => {
    write([]);
    setRecent([]);
  }, []);

  return { recent, remember, refresh, clear };
};
